// @ts-check
import dateformat from 'dateformat';
import { isCottonSession as getCottonSession } from '../helpers/featureFlags/cotton';
import apiClient from '../middleware/apiClient';

import { dispatchSendTransaction, dispatchSendErrorEvent } from './adobe';
import { sendChannelTracking } from './channelTracking';

import {
  ARRANGEMENT, SCHEDULED_PAY,
} from '../helpers/apis';
import { COTTON_ARRANGEMENT, COTTON_SCHEDULED_PAY } from '../helpers/cottonApis';
import { formatBillingAddress } from '../helpers/address';
import { getErrorFromResponse } from '../helpers/errors';
import {
  P2P_COMPLETED, P2P_FAILED, PAYMENT_SCH_COMPLETE, SPLIT_PAY_STARTED, SPLIT_PAY_ENROLLED,
  SPLIT_PAY_ERROR,
} from '../helpers/channelTracking';
import { checkPaymentQualifiesForP2P } from '../helpers/p2p';
import { flagEnabled } from '../helpers/featureFlags';


/**
 * @import { RootState } from '../types/state/rootState';
 * @import {
 *   ReviewData,
 *   FilledPaymentFormValues,
 *   PaymentFormValues,
 *   ConfirmationData,
 * } from '../types/state/arrangementState';
 * @import {
 *   FormattedBillingAddress,
 *   GetArrangementEligibilityResponse,
 *   PostArrangementPayRequest,
 *   PostArrangementPayResponse
 * } from '../types/apis/arrangement';
 * @import { Account, AccountServiceAddress } from '../types/apis/account';
 * @import { TransactionMetadata } from '../types/common';
 * @import { AnyAction } from 'redux';
 * @import { ThunkDispatch } from 'redux-thunk';
 */

/**
 * @template T
 * @typedef {Object} DispatchAction
 * @property {string} type
 * @property {T} payload
 */

/**
 * @template [ThunkReturnType=void]
 * @typedef {function(
 *   ThunkDispatch<RootState, undefined, any>,
 *   function(): RootState
 * ): ThunkReturnType} AppThunk
 */

export const PAYMENT_ARRANGEMENT = 'PAYMENT_ARRANGEMENT';
export const PAYMENT_ARRANGEMENT_SUCCESS = 'PAYMENT_ARRANGEMENT_SUCCESS';
export const PAYMENT_ARRANGEMENT_FAILURE = 'PAYMENT_ARRANGEMENT_FAILURE';

export const GET_ARRANGEMENT = 'GET_ARRANGEMENT';
export const GET_ARRANGEMENT_SUCCESS = 'GET_ARRANGEMENT_SUCCESS';
export const GET_ARRANGEMENT_FAILURE = 'GET_ARRANGEMENT_FAILURE';

/**
 * @returns {DispatchAction<Promise<GetArrangementEligibilityResponse>>}
 */
function dispatchGetArrangement(isCottonSession = false) {
  const endpoint = isCottonSession ? COTTON_ARRANGEMENT : ARRANGEMENT;
  return {
    type: GET_ARRANGEMENT,
    payload: apiClient.fetch(endpoint),
  };
}

/**
 * @param {GetArrangementEligibilityResponse} arrangement
 * @returns {DispatchAction<GetArrangementEligibilityResponse>}
 */
function dispatchGetArrangementSuccess(arrangement) {
  return {
    type: GET_ARRANGEMENT_SUCCESS,
    payload: arrangement,
  };
}

function dispatchGetArrangementFailure(error) {
  return {
    type: GET_ARRANGEMENT_FAILURE,
    payload: error,
  };
}

/**
 * @returns {AppThunk<Promise<DispatchAction<GetArrangementEligibilityResponse> | null>>}
 */
export const getArrangement = () => async (dispatch, getState) => {
  const {
    arrangement: { cached } = {},
  } = getState();

  if (cached) {
    return null;
  }
  try {
    const isCottonSession = getCottonSession();
    const response = await dispatch(dispatchGetArrangement(isCottonSession)).payload;
    return dispatch(dispatchGetArrangementSuccess(response));
  } catch (error) {
    dispatch(dispatchGetArrangementFailure(error));
    throw error;
  }
};

function dispatchPostArrangementPay(data, isCottonSession = false) {
  const {
    transactionMetadata,
    useArrangementEnrollApi,
    ...payload
  } = data;
  const arrangeEndpoint = isCottonSession ? COTTON_ARRANGEMENT : ARRANGEMENT;
  const scheduleEndpoint = isCottonSession ? COTTON_SCHEDULED_PAY : SCHEDULED_PAY;
  const endpoint = useArrangementEnrollApi ? arrangeEndpoint : scheduleEndpoint;

  if (!useArrangementEnrollApi && flagEnabled('txnDataPaymentEnabled')) {
    payload.transactionMetadata = transactionMetadata;
  }

  if (useArrangementEnrollApi) {
    payload.amount = Number(payload.amount);
  }

  return {
    type: PAYMENT_ARRANGEMENT,
    payload: apiClient.fetch(endpoint, { method: 'POST', body: JSON.stringify(payload) }),
  };
}

function dispatchPostArrangementPaySuccess(payment) {
  return {
    type: PAYMENT_ARRANGEMENT_SUCCESS,
    payload: payment,
  };
}

function dispatchPostArrangementPayFailure(error) {
  return {
    type: PAYMENT_ARRANGEMENT_FAILURE,
    payload: error,
  };
}

/**
 * @param {PostArrangementPayRequest} data
 * @param {boolean} [isSplitPayment] Whether this payment is one of a split payment; when true,
 * the action ids for split payments will be handled in the caller
 * when both payments have processed sucessfully.
 * @returns {AppThunk<Promise<PostArrangementPayResponse>>}
 */
export const postArrangementPay = (data, isSplitPayment = false) => async (dispatch, getState) => {
  try {
    const isCottonSession = getCottonSession();
    const response = await dispatch(dispatchPostArrangementPay(data, isCottonSession)).payload;
    dispatch(dispatchPostArrangementPaySuccess(response));

    if (isSplitPayment) {
      return response;
    }

    // Check if payment qualifies for P2P to send correct action ID
    const {
      bill: { bill } = {},
      arrangement: { arrangement } = {},
    } = getState();

    const actualAmount = response.amount ?? data.amount;
    const actualDate = response.date ?? data.date;

    /** @type {boolean} */
    const paymentQualifies = checkPaymentQualifiesForP2P({
      bill,
      arrangement,
      amount: actualAmount,
      date: actualDate,
    });

    dispatch(sendChannelTracking({
      id: paymentQualifies ? P2P_COMPLETED : PAYMENT_SCH_COMPLETE,
      interactionType: 'BILLING_PAYMENTS',
    }));
    return response;
  } catch (/** @type {any} */ error) {
    dispatch(sendChannelTracking({
      id: isSplitPayment ? SPLIT_PAY_ERROR : P2P_FAILED,
      error,
      interactionType: 'BILLING_PAYMENTS',
    }));
    dispatch(dispatchPostArrangementPayFailure(error));
    throw error;
  }
};

// REVIEW_PAYMENT
export const REVIEW_PAYMENT = 'REVIEW_PAYMENT_ARRANGEMENT';

function dispatchReviewPayment(data) {
  return {
    type: REVIEW_PAYMENT,
    payload: data,
  };
}

/**
 * @param {ReviewData} values
 * @returns {AppThunk<DispatchAction<ReviewData>>}
 */
export const reviewPayment = (values = {
  type: 'single',
  payment1: {},
}) => (dispatch, getState) => {
  const {
    account: { account } = {},
    // @ts-expect-error We haven't defined `auth` in RootState yet.
    auth: { macaroon: { preferredEmail } = {} } = {},
  } = getState();

  const reviewData = {
    ...values,
    email: values.email || preferredEmail,
    billingAddress: values.payment1?.cpcData?.billingAddress
      || formatBillingAddress(account, values),
  };

  return dispatch(dispatchReviewPayment(reviewData));
};

// SUBMIT_PAYMENT
export const SUBMIT_PAYMENT = 'SUBMIT_PAYMENT_ARRANGEMENT';
export const SUBMIT_PAYMENT_CONFIRMATION = 'SUBMIT_PAYMENT_ARRANGEMENT_CONFIRMATION';

function dispatchSubmitPayment(data) {
  return {
    type: SUBMIT_PAYMENT,
    payload: data,
  };
}

function dispatchSubmitPaymentConfirmation(data) {
  return {
    type: SUBMIT_PAYMENT_CONFIRMATION,
    payload: data,
  };
}

/**
 * @param {unknown} paymentFormValues
 * @returns {paymentFormValues is FilledPaymentFormValues}
 */
function isFullPaymentFormObject(paymentFormValues) {
  if (!paymentFormValues || typeof paymentFormValues !== 'object') {
    return false;
  }

  return (
    'amount' in paymentFormValues
    && 'date' in paymentFormValues
  );
}

/**
 * @param {ThunkDispatch<RootState, undefined, any>} dispatch
 * @param {Account | undefined} account
 * @param {PaymentFormValues & {
 *   transactionMetadata?: TransactionMetadata,
 *   useArrangementEnrollApi?: boolean,
 *   eligibilityCheckId?: string
 * }} paymentFormValues
 * @param {boolean} [isSplitPayment] Whether this payment is one of a split payment
 * @returns {Promise<PostArrangementPayResponse>}
 */
async function submitPaymentFormValues(dispatch, account, paymentFormValues, isSplitPayment) {
  const { transactionMetadata, useArrangementEnrollApi, eligibilityCheckId } = paymentFormValues;
  if (!isFullPaymentFormObject(paymentFormValues)) {
    throw new Error('Incomplete payment form data for payment submission');
  }

  /** @type {FormattedBillingAddress} */
  const billingAddress = formatBillingAddress(account, paymentFormValues.cpcData?.billingAddress);

  const response = await dispatch(postArrangementPay({
    amount: paymentFormValues.amount,
    date: dateformat(paymentFormValues.date, 'yyyy-mm-dd'),
    token: paymentFormValues.cpcData?.token,
    billingAddress,
    transactionMetadata,
    useArrangementEnrollApi,
    eligibilityCheckId,
  }, isSplitPayment));

  if (!response) {
    throw new Error('No response from payment submission');
  }

  return response;
}

/**
 * @param {ReviewData} review
 * @returns {{ payment1: FilledPaymentFormValues, payment2?: FilledPaymentFormValues }}
 */
function getPaymentFormValues(review) {
  if (!isFullPaymentFormObject(review.payment1)) {
    throw new Error('No review data available for payment submission');
  }

  if (review.type === 'split') {
    if (!isFullPaymentFormObject(review.payment2)) {
      throw new Error('Split payment review data is incomplete for payment submission');
    }
    return {
      payment1: review.payment1,
      payment2: review.payment2,
    };
  }

  return { payment1: review.payment1 };
}

/**
 * Used to resubmit a failed payment of a split payment
 * @param {Object} props
 * @param {PaymentFormValues} props.newPayment
 * @param {PaymentFormValues} props.existingPayment
 * @param {TransactionMetadata} [props.transactionMetadata]
 * @returns {AppThunk<Promise<ConfirmationData>>}
 */
export function submitErroredPayment({ newPayment, existingPayment, transactionMetadata }) {
  return async (dispatch, getState) => {
    const {
      account: { account },
      bill: { bill } = {},
      arrangement: {
        review,
        arrangement: arrangementEligibility,
      } = {},
    } = getState();

    const existingConfirmationNumber = existingPayment.confirmationNumber;

    if (
      !review
      || !isFullPaymentFormObject(existingPayment)
      || !isFullPaymentFormObject(newPayment)
    ) {
      throw new Error('No review data available for payment submission');
    }

    dispatch(dispatchSubmitPayment(review));

    // The two payments together make up the split payment, so eligibility depends on
    // their combined amount rather than newPayment's amount alone.
    const splitPaymentQualifies = checkPaymentQualifiesForP2P({
      bill,
      arrangement: arrangementEligibility,
      amount: Number(existingPayment.amount) + Number(newPayment.amount),
      date: newPayment.date,
    });

    /** @type {ConfirmationData} */
    const confirm = {
      email: review.email ?? '',
      isPayLater: false,
      payment1: {
        amount: existingPayment.amount,
        date: existingPayment.date,
        paymentMethodOption: existingPayment.cpcData?.paymentMethodOption,
        confirmationNumber: existingConfirmationNumber,
      },
      payment2: {
        amount: newPayment.amount,
        date: newPayment.date,
        paymentMethodOption: newPayment.cpcData?.paymentMethodOption,
      },
    };

    try {
      const response = await submitPaymentFormValues(dispatch, account, {
        ...newPayment,
        ...(transactionMetadata ? { transactionMetadata } : {}),
      }, true);
      if (confirm.payment2) {
        confirm.payment2.confirmationNumber = response.confirmationNumber && `#${response.confirmationNumber}`;
      }

      // Both payments have now succeeded, so the arrangement is truly enrolled or complete.
      dispatch(sendChannelTracking({
        id: splitPaymentQualifies ? SPLIT_PAY_ENROLLED : PAYMENT_SCH_COMPLETE,
        interactionType: 'BILLING_PAYMENTS',
      }));
      if (splitPaymentQualifies) {
        dispatch(sendChannelTracking({
          id: P2P_COMPLETED,
          interactionType: 'BILLING_PAYMENTS',
        }));
      }

      dispatch(dispatchSendTransaction({
        name: 'self service transaction',
        action: 'self service transaction',
        transaction: 'billing:paymentArrangement:payment:confirm',
      }));

      dispatch(dispatchSubmitPaymentConfirmation(confirm));

      return confirm;
    } catch (error) {
      const errorDetail = getErrorFromResponse(error);
      const isAuthPaymentError = errorDetail.code === 'CON500.PAYMENT.50.52'
          || errorDetail.code === 'CON500.PAYMENT.50.53'
          || errorDetail.code === 'PAYMENT-5052';
      dispatch(dispatchSendErrorEvent(errorDetail));
      throw Object.assign({}, errorDetail, { isAuthPaymentError });
    }
  };
}

/**
 * @param {Object} props
 * @param {TransactionMetadata} [props.transactionMetadata]
 * @returns {AppThunk<Promise<ConfirmationData>>}
 */
export const submitPayment = ({ transactionMetadata } = {}) => async (dispatch, getState) => {
  const {
    account: { account },
    bill: { bill } = {},
    arrangement: {
      review,
      arrangement: arrangementEligibility,
    } = {},
  } = getState();

  dispatch(dispatchSubmitPayment(review));

  const eligibilityCheckId = arrangementEligibility?.customerEligibilityChecked?.eligibilityCheckId;
  const useArrangementEnrollApi = review?.isPayLater;

  if (!review) {
    throw new Error('No review data available for payment submission');
  }

  const {
    payment1, payment2,
  } = getPaymentFormValues(review);

  const isSplitPayment = review.type === 'split';

  if (isSplitPayment) {
    dispatch(sendChannelTracking({
      id: SPLIT_PAY_STARTED,
      interactionType: 'BILLING_PAYMENTS',
    }));
  }

  // Split payments only count as an arrangement when their combined amount qualifies;
  // otherwise each payment is tracked as its own completed scheduled payment.
  const splitPaymentQualifies = isSplitPayment && checkPaymentQualifiesForP2P({
    bill,
    arrangement: arrangementEligibility,
    amount: Number(payment1.amount) + (payment2 ? Number(payment2.amount) : 0),
    date: payment1.date,
  });

  /** @type {function(PaymentFormValues): Promise<PostArrangementPayResponse>} */
  const submitFn = values => submitPaymentFormValues(dispatch, account, {
    ...values,
    ...(transactionMetadata ? { transactionMetadata } : {}),
  }, isSplitPayment);

  const payment1Enhanced = {
    ...payment1,
    ...(useArrangementEnrollApi ? { useArrangementEnrollApi, eligibilityCheckId } : {}),
  };

  /** @type {ConfirmationData} */
  const confirm = {
    failedPayment: undefined,
    isPayLater: review.isPayLater ?? false,
    email: review.email ?? '',
    payment1: {
      amount: payment1.amount,
      date: payment1.date,
      paymentMethodOption: payment1.cpcData?.paymentMethodOption,
    },
    ...(payment2 && {
      payment2: {
        amount: payment2.amount,
        date: payment2.date,
        paymentMethodOption: payment2.cpcData?.paymentMethodOption,
      },
    }),
  };

  const promises = {
    payment1: () => submitFn(payment1Enhanced),
    payment2: payment2
      ? () => submitFn(payment2)
      : () => Promise.resolve(undefined),
  };

  // Process payment requests sequentially
  const executePaymentSettled = async (fn) => {
    try {
      return { status: 'fulfilled', value: await fn() };
    } catch (reason) {
      return { status: 'rejected', reason };
    }
  };

  const result1 = await executePaymentSettled(promises.payment1);
  const result2 = await executePaymentSettled(promises.payment2);

  const payment1Failed = result1.status === 'rejected';
  const payment2Failed = result2?.status === 'rejected';

  if (payment1Failed || payment2Failed) {
    const errors = [
      payment1Failed && result1.reason,
      payment2Failed && result2?.reason,
    ].filter(Boolean);

    let isAuthPaymentError = false;
    errors.forEach((error) => {
      const errorDetail = getErrorFromResponse(error);
      isAuthPaymentError = isAuthPaymentError || (
        errorDetail.code === 'CON500.PAYMENT.50.52'
          || errorDetail.code === 'CON500.PAYMENT.50.53'
          || errorDetail.code === 'PAYMENT-5052'
      );
      dispatch(dispatchSendErrorEvent(errorDetail));
    });

    let failedPayment;
    if (payment1Failed && payment2Failed || (review.type === 'single' && payment1Failed)) {
      failedPayment = 'both';
    } else if (payment1Failed) {
      failedPayment = 'payment1';
      if (confirm.payment2 && result2?.status === 'fulfilled') {
        confirm.payment2.confirmationNumber = result2.value?.confirmationNumber && `#${result2.value?.confirmationNumber}`;
      }
      // Only one payment landed, so the arrangement is not truly enrolled.
      if (isSplitPayment) {
        dispatch(sendChannelTracking({
          id: PAYMENT_SCH_COMPLETE,
          interactionType: 'BILLING_PAYMENTS',
        }));
      }
    } else {
      failedPayment = 'payment2';
      if (result1.status === 'fulfilled') {
        confirm.payment1.confirmationNumber = result1.value?.confirmationNumber && `#${result1.value?.confirmationNumber}`;
      }
      // Only one payment landed, so the arrangement is not truly enrolled.
      if (isSplitPayment) {
        dispatch(sendChannelTracking({
          id: PAYMENT_SCH_COMPLETE,
          interactionType: 'BILLING_PAYMENTS',
        }));
      }
    }

    confirm.failedPayment = /** @type {ConfirmationData['failedPayment']} */ (failedPayment);

    if (failedPayment !== 'both') {
      dispatch(dispatchSubmitPaymentConfirmation(confirm));
    }

    const error = Object.assign(new Error('One or more payments failed'), {
      failedPayment,
      isAuthPaymentError,
      errors,
    });
    throw error;
  }

  const response1 = result1.value;
  const response2 = result2?.value;

  confirm.payment1.confirmationNumber = response1?.confirmationNumber && `#${response1?.confirmationNumber}`;
  if (response2 && confirm.payment2) {
    confirm.payment2.confirmationNumber = response2.confirmationNumber && `#${response2.confirmationNumber}`;
  }

  // Both payments succeeded, so the arrangement is now truly enrolled or complete.
  if (isSplitPayment) {
    const completeTrackingId = splitPaymentQualifies ? SPLIT_PAY_ENROLLED : PAYMENT_SCH_COMPLETE;

    [response1, response2].forEach(() => {
      dispatch(sendChannelTracking({
        id: completeTrackingId,
        interactionType: 'BILLING_PAYMENTS',
      }));
    });

    if (splitPaymentQualifies) {
      dispatch(sendChannelTracking({
        id: P2P_COMPLETED,
        interactionType: 'BILLING_PAYMENTS',
      }));
    }
  }

  dispatch(dispatchSendTransaction({
    name: 'self service transaction',
    action: 'self service transaction',
    transaction: 'billing:paymentArrangement:payment:confirm',
  }));

  dispatch(dispatchSubmitPaymentConfirmation(confirm));

  return confirm;
};

// RESET_PAYMENT
export const RESET_PAYMENT = 'RESET_PAYMENT_ARRANGEMENT';
/** @returns {function(ThunkDispatch<RootState, undefined, AnyAction>)} */
export const resetPayment = () => dispatch => dispatch({ type: RESET_PAYMENT });
